import type { SceneEventCue } from '../flow/types'

const requiredElement = <T extends HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector)
  if (!element) throw new Error(`Missing required scene-event element: ${selector}`)
  return element
}

export class SceneEventOverlay {
  private readonly app = requiredElement<HTMLElement>('#app')
  private readonly overlay = requiredElement<HTMLElement>('#scene-event-overlay')
  private readonly text = requiredElement<HTMLElement>('#scene-event-text')
  private timer: number | null = null
  private playbackResolve: (() => void) | null = null

  public play(cue: SceneEventCue): Promise<void> {
    this.finishPlayback()
    this.text.textContent = cue.text.trim() || 'Scene event'
    this.overlay.dataset.event = cue.event
    this.overlay.dataset.visible = 'true'
    this.overlay.setAttribute('aria-hidden', 'false')
    this.app.dataset.sceneEventVisible = 'true'

    if (cue.vibrate && typeof navigator.vibrate === 'function') {
      navigator.vibrate([85, 45, 85])
    }

    return new Promise((resolve) => {
      this.playbackResolve = resolve
      this.timer = window.setTimeout(this.finishPlayback, Math.max(300, cue.durationMs))
    })
  }

  public hide(): void {
    this.overlay.dataset.visible = 'false'
    this.overlay.setAttribute('aria-hidden', 'true')
    this.app.dataset.sceneEventVisible = 'false'
  }

  private readonly finishPlayback = (): void => {
    if (this.timer !== null) window.clearTimeout(this.timer)
    this.timer = null
    this.hide()
    const resolve = this.playbackResolve
    this.playbackResolve = null
    resolve?.()
  }

  public dispose(): void {
    this.finishPlayback()
  }
}
